import { SearchTrip } from "@/services/purchase.service";
import { useAuthStore } from "@/stores/auth.store";
import type { TripType } from "@/types/port";
import type { PortOption } from "@/types/search";
import type {
  PassengerCounts,
  TicketTypeList,
} from "@/types/init";
import { getPassengerCountKey } from "@/utils/passenger-type";
import type {
  PassengerList,
  ScheduleTripRequest,
} from "@/types/scheduletrip";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type Props = {
  tripType: TripType;
  origin?: PortOption;
  destination?: PortOption;
  departDate: Date;
  returnDate?: Date;
  counts: PassengerCounts;
  passengerTypes: TicketTypeList[];
};

// Use for building the ScheduleTripRequest from URLSearchParams in SelectTripPage
export const buildScheduleTripSearchRequestFromParams = (
  searchParams: URLSearchParams,
  passengerTypes: TicketTypeList[],
): ScheduleTripRequest => {
  const tripType = searchParams.get("tripType") ?? "one-way";

  const counts: PassengerCounts = {
    adultMYS: Number(searchParams.get("adultMYS") ?? 0),
    childMYS: Number(searchParams.get("childMYS") ?? 0),
    infantMYS: Number(searchParams.get("infantMYS") ?? 0),

    adultForeign: Number(searchParams.get("adultForeign") ?? 0),
    childForeign: Number(searchParams.get("childForeign") ?? 0),
    infantForeign: Number(searchParams.get("infantForeign") ?? 0),

    local: Number(searchParams.get("local") ?? 0),
    foc: Number(searchParams.get("foc") ?? 0),
    worker: Number(searchParams.get("worker") ?? 0),
  };

  const passengerMap = new Map<string, PassengerList>();

  passengerTypes.forEach((ticket) => {
    const key = getPassengerCountKey(ticket);

    if (!key) return;

    const quantity = counts[key] ?? 0;

    const passengerKey = `${ticket.mdTicketTypeId}:${key}`;

    if (quantity > 0 && !passengerMap.has(passengerKey)) {
      passengerMap.set(passengerKey, {
        mdTicketTypeId: ticket.mdTicketTypeId,
        quantity,
      });
    }
  });

  return {
    agentCompanyId:
      useAuthStore.getState().user?.agentCompanyId ?? "",

    agentCompanySellerId:
      useAuthStore.getState().user?.agentCompanySellerId ?? "",

    mdTerminalIdFrom:
      searchParams.get("originId") ?? "",

    mdTerminalIdTo:
      searchParams.get("destinationId") ?? "",

    departureDate:
      searchParams.get("departDate") ?? "",

    ...(tripType === "return" && searchParams.get("returnDate")
      ? {
          returnDate: searchParams.get("returnDate")!,
        }
      : {}),

    passengerList: Array.from(passengerMap.values()),
  };
};

export function useScheduleTripSearch() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const buildSearchParams = ({
    tripType,
    origin,
    destination,
    departDate,
    returnDate,
    counts,
  }: Props) => {
    const params = new URLSearchParams();

    params.set(
      "tripType",
      tripType === "return" ? "return" : "one-way",
    );

    params.set("origin", origin?.name ?? "");
    params.set("destination", destination?.name ?? "");

    params.set("originId", origin?.id ?? "");
    params.set("destinationId", destination?.id ?? "");

    params.set(
      "departDate",
      format(departDate, "yyyy-MM-dd"),
    );

    if (tripType === "return" && returnDate) {
      params.set(
        "returnDate",
        format(returnDate, "yyyy-MM-dd"),
      );
    }

    Object.entries(counts).forEach(([key, value]) => {
      params.set(key, String(value));
    });

    const totalPax = Object.values(counts).reduce(
      (sum, value) => sum + value,
      0,
    );

    params.set("passengers", String(totalPax));

    return params;
  };

  const buildScheduleTripSearchRequest = ({
    tripType,
    origin,
    destination,
    departDate,
    returnDate,
    counts,
    passengerTypes,
  }: Props): ScheduleTripRequest => {
    const passengerMap = new Map<string, PassengerList>();

    passengerTypes.forEach((ticket) => {
      const key = getPassengerCountKey(ticket);

      if (!key) {
        return;
      }

      const quantity = counts[key] ?? 0;

      const passengerKey = `${ticket.mdTicketTypeId}:${key}`;

      if (quantity > 0 && !passengerMap.has(passengerKey)) {
        passengerMap.set(passengerKey, {
          mdTicketTypeId: ticket.mdTicketTypeId,
          quantity,
        });
      }
    });

    const passengerList = Array.from(passengerMap.values());

    return {
      agentCompanyId:
        useAuthStore.getState().user?.agentCompanyId || "",

      agentCompanySellerId:
        useAuthStore.getState().user?.agentCompanySellerId || "",

      mdTerminalIdFrom: origin?.id || "",

      mdTerminalIdTo: destination?.id || "",

      departureDate: format(
        departDate,
        "yyyy-MM-dd",
      ),

      ...(tripType === "return" && returnDate
        ? {
            returnDate: format(
              returnDate,
              "yyyy-MM-dd",
            ),
          }
        : {}),

      passengerList,
    };
  };

  const handleSearch = async (values: Props) => {
    if (loading) return false;

    try {
      setLoading(true);

      const params = buildSearchParams(values);
      const payload =  buildScheduleTripSearchRequest(values);
      const scheduleData = await SearchTrip(payload);

      navigate(
        `/purchase-ticket/select-trip?${params.toString()}`,
        {
          state: {
            scheduleData,
          },
        },
      );

      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Search failed",
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSearch,
    loading,
  };
}